import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_FORM_SUBMISSION, GET_FORM } from "../../graphql/operations";
import { Helmet } from "react-helmet-async";
import PrintIcon from "@mui/icons-material/Print";

const PrintableSubmission: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();

  // Fetch submission data
  const {
    data: submissionData,
    loading: submissionLoading,
    error: submissionError,
  } = useQuery(GET_FORM_SUBMISSION, {
    variables: { submissionId },
    skip: !submissionId,
  });

  // Get submission from response
  const items = submissionData?.getFormSubmission?.items || [];
  const submission = items[0];

  // Fetch the original form template using formId from submission
  const {
    data: formData,
    loading: formLoading,
    error: formError,
  } = useQuery(GET_FORM, {
    variables: { id: submission?.formId },
    skip: !submission?.formId,
  });

  const form = formData?.getForm;

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (submissionLoading || formLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  if (submissionError || formError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error: {submissionError?.message || formError?.message}
      </Alert>
    );
  }

  if (!submission) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Submission not found.
      </Alert>
    );
  }

  if (!form) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Original form template not found.
      </Alert>
    );
  }

  // Create a map of submission data for easy lookup
  const submissionDataMap: Record<string, string> = {};
  submission.data?.forEach((field: any) => {
    submissionDataMap[field.label] = field.value;
  });

  // Group fields by section for display
  const sectionsWithData =
    form.sections
      ?.map((section: any) => {
        const sectionFields =
          section.fields
            ?.map((field: any) => ({
              ...field,
              value: submissionDataMap[field.label] || "Not provided",
            }))
            .filter((field: any) => field) || [];

        return {
          ...section,
          fields: sectionFields,
        };
      })
      .filter((section: any) => section.fields?.length > 0) || [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>{`Print Submission - ${submission.formTitle}`}</title>
      </Helmet>

      {/* Print-specific styles */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              margin: 0;
              padding: 20px;
              -webkit-print-color-adjust: exact;
            }
            .print-page {
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          }
          @page {
            margin: 20mm;
          }
        `}
      </style>

      {/* Action buttons - hidden when printing */}
      <Box className="no-print" sx={{ mb: 2 }}>
        <Button onClick={handleBack} sx={{ mr: 2 }}>
          ← Back
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
        >
          Print
        </Button>
      </Box>

      {/* Printable content */}
      <Paper className="print-page" sx={{ p: 4 }}>
        {/* AdminHeader */}
        <Box
          sx={{
            textAlign: "center",
            mb: 4,
            borderBottom: "2px solid #f0f0f0",
            pb: 3,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {submission.formTitle}
          </Typography>
          <Typography variant="h6" color="textSecondary">
            Form Submission Details
          </Typography>
        </Box>

        {/* Submission Metadata */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}
          >
            Submission Information
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 2,
              mt: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Submission ID
              </Typography>
              <Typography variant="body1">{submission.submissionId}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Submitted By
              </Typography>
              <Typography variant="body1">{submission.submittedBy}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Submission Date
              </Typography>
              <Typography variant="body1">
                {new Date(submission.submittedAt).toLocaleDateString()} at{" "}
                {new Date(submission.submittedAt).toLocaleTimeString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color:
                    submission.status === "APPROVED"
                      ? "success.main"
                      : submission.status === "REJECTED"
                      ? "error.main"
                      : "text.primary",
                  fontWeight: "bold",
                }}
              >
                {submission.status}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Form Version
              </Typography>
              <Typography variant="body1">{submission.formVersion}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Form Data Grouped by Sections */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}
          >
            Form Data
          </Typography>

          {sectionsWithData.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No form data available.
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {sectionsWithData.map((section: any, sectionIndex: number) => (
                <Box
                  key={section.id || sectionIndex}
                  sx={{
                    mb: 4,
                    pb: 3,
                    borderBottom:
                      sectionIndex < sectionsWithData.length - 1
                        ? "2px solid #e0e0e0"
                        : "none",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "primary.main" }}
                  >
                    {section.title}
                  </Typography>

                  {section.subtitle && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 3 }}
                    >
                      {section.subtitle}
                    </Typography>
                  )}

                  <Box sx={{ pl: 2 }}>
                    {section.fields.map((field: any, fieldIndex: number) => (
                      <Box
                        key={field.label || fieldIndex}
                        sx={{
                          mb: 3,
                          pb: 2,
                          borderBottom:
                            fieldIndex < section.fields.length - 1
                              ? "1px solid #f5f5f5"
                              : "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {field.label}
                            {field.required && (
                              <Typography
                                component="span"
                                color="error"
                                sx={{ ml: 1 }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {field.type}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            p: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            minHeight: "40px",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {field.value || "Not provided"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: 6,
            pt: 3,
            borderTop: "2px solid #f0f0f0",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Printed on {new Date().toLocaleDateString()} at{" "}
            {new Date().toLocaleTimeString()}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            Submission ID: {submission.submissionId}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            Form ID: {submission.formId} • Version: {submission.formVersion}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrintableSubmission;
